import { useBookSkiPassStore } from '@/store/book-ski-pass.store';

export type GtmEcommerceItem = {
  item_id: string;
  item_name: string;
  price: number;
  currency: string;
  quantity?: number;
};

type GtmEventPayload = {
  event: 'view_item' | 'add_to_cart' | 'begin_checkout' | 'purchase';
  ecommerce: {
    currency: string;
    items: GtmEcommerceItem[];
    value?: number;
    transaction_id?: string;
  };
};

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    __gtmSentEventKeys?: Set<string>;
  }
}

const GTM_DEBUG_STORAGE_KEY = 'gtm_debug';
const GTM_BOOKING_REF_KEY = 'gtm_booking_ref';

/** True after the Alps2Alps session request has settled, with or without an id. */
let alps2alpsSessionChecked = false;

function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  const debugFromStorage = window.localStorage.getItem(GTM_DEBUG_STORAGE_KEY);
  return debugFromStorage === '1' || debugFromStorage === 'true';
}

export type JourneyTrackingFields = {
  booking_ref?: string;
  promocode?: string;
  userId?: string | number;
};

type ResortTrackingPayload =
  | { event: 'user_id_set'; userId: string | number }
  | {
      event: 'resort_selection';
      userId: string | number;
      booking_ref: string;
      promocode?: string;
    };

function nonEmpty(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function rememberedBookingRef(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return nonEmpty(window.sessionStorage.getItem(GTM_BOOKING_REF_KEY));
}

/** Keep the validated reference for later events, even if the checkout order has none. */
export function rememberBookingReference(bookingRef: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  const next = nonEmpty(bookingRef ?? undefined);
  if (!next) {
    window.sessionStorage.removeItem(GTM_BOOKING_REF_KEY);
    return;
  }
  window.sessionStorage.setItem(GTM_BOOKING_REF_KEY, next);
  const store = useBookSkiPassStore.getState();
  if (nonEmpty(store.bookingReferenceNumber) !== next) {
    store.setBookingReferenceNumber(next);
  }
  if (nonEmpty(store.stepOneFormFieldsValues.bookingReference) !== next) {
    store.updateStepOneFormField('bookingReference', next);
  }
}

export function trackingBookingReference(): string | undefined {
  const state = useBookSkiPassStore.getState();
  return (
    rememberedBookingRef() ||
    nonEmpty(state.bookingReferenceNumber) ||
    nonEmpty(state.stepOneFormFieldsValues.bookingReference)
  );
}

function loyaltyUserId(): string | number | undefined {
  const userId = useBookSkiPassStore.getState().alps2alpsUserId;
  return userId != null && userId !== '' ? userId : undefined;
}

/** Loyalty profile id, or the booking reference once the session check found no id. */
function trackingUserId(
  explicit: string | number | null | undefined,
  bookingRef: string | undefined
): string | number | undefined {
  if (explicit != null && explicit !== '') return explicit;
  const loyaltyId = loyaltyUserId();
  if (loyaltyId != null) return loyaltyId;
  if (!alps2alpsSessionChecked) return undefined;
  return bookingRef;
}

export function journeyTrackingFields(context?: Partial<JourneyTrackingFields>): JourneyTrackingFields {
  const state = useBookSkiPassStore.getState();
  const bookingRef = nonEmpty(context?.booking_ref) || trackingBookingReference();
  const promoCode = nonEmpty(context?.promocode) || nonEmpty(state.stepOneFormFieldsValues.promoCode);
  const userId = trackingUserId(context?.userId, bookingRef);

  const fields: JourneyTrackingFields = {};
  if (bookingRef) fields.booking_ref = bookingRef;
  if (promoCode) fields.promocode = promoCode;
  if (userId != null && userId !== '') fields.userId = userId;
  return fields;
}

function pushDataLayer(payload: GtmEventPayload, context?: Partial<JourneyTrackingFields>): void {
  if (typeof window === 'undefined') return;
  pushResortSelectionEvents();
  window.dataLayer = window.dataLayer ?? [];
  const tracked = {
    ...journeyTrackingFields(context),
    ...payload,
  };
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(tracked as unknown as Record<string, unknown>);
  if (isDebugEnabled()) {
    console.info('[GTM:dataLayer.push]', tracked);
  }
}

export function pushDataLayerOnce(
  key: string,
  payload: GtmEventPayload,
  context?: Partial<JourneyTrackingFields>
): void {
  if (typeof window === 'undefined') return;
  window.__gtmSentEventKeys = window.__gtmSentEventKeys ?? new Set<string>();
  if (window.__gtmSentEventKeys.has(key)) return;
  window.__gtmSentEventKeys.add(key);
  pushDataLayer(payload, context);
}

function pushUserIdSet(userId: string | number): void {
  if (typeof window === 'undefined') return;
  window.__gtmSentEventKeys = window.__gtmSentEventKeys ?? new Set<string>();
  if (window.__gtmSentEventKeys.has('user_id_set')) return;

  window.__gtmSentEventKeys.add('user_id_set');
  window.dataLayer = window.dataLayer ?? [];
  const userIdSet: ResortTrackingPayload = { event: 'user_id_set', userId };
  window.dataLayer.push(userIdSet as unknown as Record<string, unknown>);
  if (isDebugEnabled()) {
    console.info('[GTM:dataLayer.push]', userIdSet);
  }
}

function isTripDetailsPath(): boolean {
  return typeof window !== 'undefined' && window.location.pathname === '/trip-details';
}

/** Trip details opened without a booking reference. The saved customer id is enough. */
export function pushTripDetailsUserId(): void {
  const userId = journeyTrackingFields().userId;
  if (userId == null || userId === '') return;
  pushUserIdSet(userId);
}

/** After a valid reference opens /trip-details. resort_selection still needs that reference. */
export function pushResortSelectionEvents(bookingRef?: string): void {
  if (nonEmpty(bookingRef)) rememberBookingReference(bookingRef);
  if (typeof window === 'undefined') return;
  const fields = journeyTrackingFields(
    bookingRef != null ? { booking_ref: bookingRef } : undefined
  );
  if (fields.userId == null || fields.userId === '' || !fields.booking_ref) return;

  window.__gtmSentEventKeys = window.__gtmSentEventKeys ?? new Set<string>();
  if (window.__gtmSentEventKeys.has('user_id_set') && window.__gtmSentEventKeys.has('resort_selection')) {
    return;
  }

  pushUserIdSet(fields.userId);
  if (window.__gtmSentEventKeys.has('resort_selection')) return;

  window.__gtmSentEventKeys.add('resort_selection');
  window.dataLayer = window.dataLayer ?? [];
  const resortSelection: ResortTrackingPayload = {
    event: 'resort_selection',
    userId: fields.userId,
    booking_ref: fields.booking_ref,
    ...(fields.promocode ? { promocode: fields.promocode } : {}),
  };
  window.dataLayer.push(resortSelection as unknown as Record<string, unknown>);
  if (isDebugEnabled()) {
    console.info('[GTM:dataLayer.push]', resortSelection);
  }
}

function pushTripDetailsIdentity(): void {
  if (!isTripDetailsPath()) return;
  pushTripDetailsUserId();
  pushResortSelectionEvents();
}

/** The session request finished. A missing id may use the booking reference. */
export function markAlps2AlpsSessionChecked(): void {
  alps2alpsSessionChecked = true;
  pushTripDetailsIdentity();
}

export function resetAlps2AlpsSessionChecked(): void {
  alps2alpsSessionChecked = false;
}

/** Store the loyalty profile id. Push only after /trip-details is already open. */
export function rememberAlps2AlpsUserId(userId: number | null | undefined): void {
  if (typeof userId !== 'number' || !Number.isFinite(userId)) return;
  useBookSkiPassStore.getState().setAlps2AlpsUserId(userId);
  alps2alpsSessionChecked = true;
  pushTripDetailsIdentity();
}

export function centsToMoney(cents: number): number {
  return Number((cents / 100).toFixed(2));
}